import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { formatDate } from '@angular/common';
import { UnpaidPaymentsCalculationService } from '../../unpaid-payments-calculation.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { toast } from 'ngx-sonner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as XLSX from 'xlsx';
import {
  DENEFITS_CATEGORIES,
  DenefitsBalanceSnapshot,
  DenefitsCategoryOption,
  DenefitsDashboardRow,
  DenefitsDashboardSection,
  DenefitsDetailMode,
  DenefitsView,
  EMPTY_DENEFITS_BALANCE,
  getDenefitsCategoryById,
  getDenefitsCategoryKey,
  getDenefitsCategoryDisplay,
  getDenefitsCategoryLabel,
  getDenefitsStatusOptions,
  getVersionDateRange,
  getVersionLabel,
  isPartnerFilter,
  normalizeToneClasses,
  showsContractStatusPill as shouldShowContractStatusPill,
} from './denefits-unpaid.config';
import {
  DenefitsListRequest,
  DenefitsPayRequest,
  DenefitsUnpaidApiService,
} from './denefits-unpaid-api.service';
import { MatDialog } from '@angular/material/dialog';
import { UnpaidPaymentsModalService } from '../../unpaid-payments-modal.service';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '../../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { UNPAID_PAYMENTS_BULK_SELECTION_STYLES } from '../../unpaid-payments-bulk-selection.styles';
import { UnpaidPaymentsIsDummyService } from '../../unpaid-payments-is-dummy.service';
import { UnpaidPaymentsAccessService } from '../../unpaid-payments-access.service';
import {
  buildIsDummyQueryParam,
  getDefaultIsDummy,
  parseQueryIsDummy,
} from '../../unpaid-payments-route-query.util';
import { DropdownOption } from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { ProgressBarData } from '../../../../../shared/components/data-progress-bar/data-progress-bar.component';
import { TableHeader } from '../../../../../shared/components/tables/paginated-table/paginated-table.component';
import {
  DEFAULT_DETAILS_CONTRACT_COLUMNS,
  changeDenefitsBusinessColumns,
  changeDenefitsBusinessDetailsColumns,
  changeDenefitsDashboardColumns,
  changeDenefitsListingColumns,
} from './denefits-unpaid-columns.util';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PaginationComponent } from '../../../../../shared/components/pagination/pagination.component';
import { DataProgressBarComponent } from '../../../../../shared/components/data-progress-bar/data-progress-bar.component';
import {
  DropdownComponent,
  DropdownTriggerDirective,
} from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import { ScrollTableComponent } from '../../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { SelectionComponent } from '../../../../../shared/components/ui/selection/selection.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { PaginatedTableComponent } from '../../../../../shared/components/tables/paginated-table/paginated-table.component';
import { escapeHtml } from '../../../../../shared/utils/html-escape';
import { ToggleComponent } from '../../../../../shared/components/ui/toggle/toggle.component';
import { UnpaidPaymentsIsDummyToggleComponent } from '../../components/unpaid-payments-is-dummy-toggle/unpaid-payments-is-dummy-toggle.component';
import { TooltipComponent } from '../../../../../shared/components/ui/tooltip/tooltip.component';
interface DenefitsDashboardTableSection extends DenefitsDashboardSection {
  headers: TableHeader[];
  tableRows: Record<string, unknown>[];
}

interface RouteState {
  view: DenefitsView;
  detailMode: DenefitsDetailMode;
  filterId: number;
  category: string;
  entityId: string | null;
  page: number;
  versionType: number;
  search: string;
  statusFilter: string;
  vipOnly: boolean;
  urgentOnly: boolean;
  orpFilter: number;
  instantClaim: number;
  paymentType: number;
  challengeType: number;
  startDate: string;
  endDate: string;
  isDummy: boolean;
  lendeeShareType: number;
  lendeePaymentStatus: number;
}

interface AllocationLegendRow {
  label: string;
  amount: number;
  color: string;
}

@Component({
  selector: 'app-unpaid-payments-denefits',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaginationComponent,
    DataProgressBarComponent,
    DropdownComponent,
    DropdownTriggerDirective,
    IconComponentV2,
    ScrollTableComponent,
    SelectionComponent,
    ButtonComponent,
    PaginatedTableComponent,
    ToggleComponent,
    UnpaidPaymentsIsDummyToggleComponent,
    TooltipComponent,
  ],
  templateUrl: './unpaid-payments-denefits.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [UNPAID_PAYMENTS_BULK_SELECTION_STYLES],
})
export class UnpaidPaymentsDenefitsComponent implements OnInit {
  private readonly productKey = 'denefits';
  private readonly destroyRef = inject(DestroyRef);
  private readonly isDummyService = inject(UnpaidPaymentsIsDummyService);
  private readonly accessService = inject(UnpaidPaymentsAccessService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly dialog = inject(MatDialog);
  readonly categories = DENEFITS_CATEGORIES;
  readonly visibleCategories = signal<DenefitsCategoryOption[]>([]);
  readonly pageSize = 10;
  readonly versionDropdownOptions: DropdownOption[] = [
    { value: '0', label: 'ALL' },
    { value: '1', label: 'V1' },
    { value: '2', label: 'V2' },
  ];
  readonly orpDropdownOptions: DropdownOption[] = [
    { value: '-1', label: 'All' },
    { value: '1', label: 'Yes' },
    { value: '2', label: 'No' },
  ];
  readonly instantClaimDropdownOptions: DropdownOption[] = [
    { value: '-1', label: 'All' },
    { value: '1', label: 'Yes' },
    { value: '2', label: 'No' },
  ];
  readonly paymentTypeDropdownOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: 'PP' },
    { value: '2', label: 'AR' },
    { value: '3', label: 'PP Subscription' },
  ];
  readonly challengeDropdownOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: '$15 Challenge' },
    { value: '2', label: '$100 Challenge' },
  ];

  readonly lendeeShareType = signal(0);
  readonly lendeePaymentStatus = signal(0);

  readonly lendeeShareTypeOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: 'Business Share' },
    { value: '2', label: 'Supporter Share' },
    { value: '3', label: 'Unallocated Share' },
  ];

  readonly lendeePaymentStatusOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: 'Paid' },
    { value: '2', label: 'Unpaid' },
  ];

  readonly isLendeeFilter = computed(() => this.filterId() === 12);

  readonly lendeeTableHeaders: TableHeader[] = [
    { key: 'serialNo', label: 'S No.', sortable: false, colClass: 'whitespace-nowrap w-16' },
    { key: 'payableAmount', label: 'Payable Amount', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'shareType', label: 'Share Type', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'lendeeStatus', label: 'Status', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'contractDetails', label: 'Contract Details', sortable: false, colClass: 'min-w-[200px] w-px' },
    { key: 'businessDetails', label: 'Business Details', sortable: false, colClass: 'min-w-[200px] w-px' },
    { key: 'paymentDetails', label: 'Supporter Details', sortable: false, colClass: 'min-w-[200px] w-px' },
    { key: 'action', label: 'Action', sortable: false, align: 'text-left', colClass: 'whitespace-nowrap w-px' },
  ];

  readonly dashboardLoading = signal(false);
  readonly balanceLoading = signal(false);
  readonly loading = signal(false);
  readonly submitting = signal(false);
  readonly tableBusy = computed(() => this.loading() || this.submitting());
  readonly tableBusyMessage = computed(() =>
    this.submitting() ? 'Processing payments...' : `Loading ${this.viewLabel}...`
  );
  readonly exporting = signal(false);
  readonly openActionMenuRowId = signal<string | null>(null);
  readonly openActionMenuRow = signal<any | null>(null);
  readonly actionMenuPosition = signal<{ top: number; left: number } | null>(null);
  readonly openContractPopoverRowId = signal<string | null>(null);
  readonly openContractPopoverRow = signal<any | null>(null);
  readonly contractPopoverPosition = signal<{ top: number; left: number } | null>(null);

  @ViewChild('actionMenuPanel') private actionMenuPanel?: ElementRef<HTMLElement>;
  @ViewChild('contractPopoverPanel') private contractPopoverPanel?: ElementRef<HTMLElement>;
  private actionMenuTrigger: HTMLElement | null = null;
  private contractPopoverTrigger: HTMLElement | null = null;
  private contractPopoverHideTimeout?: ReturnType<typeof setTimeout>;

  private readonly actionMenuWidth = 160;
  private readonly actionMenuGap = 4;
  private readonly actionMenuViewportMargin = 8;
  private readonly actionMenuItemHeight = 40;
  private readonly actionMenuPadding = 8;
  private readonly actionMenuBottomBarReserve = 88;
  private readonly contractPopoverWidth = 220;
  private readonly contractPopoverGap = 4;
  private readonly contractPopoverEstimatedHeight = 200;
  readonly exportDropdownOptions: DropdownOption[] = [
    { value: 'xlsx', label: 'Export XLSX', icon: 'fileText' },
    { value: 'csv', label: 'Export CSV', icon: 'fileText' },
  ];
  readonly view = signal<DenefitsView>('dashboard');
  readonly detailMode = signal<DenefitsDetailMode>('all');
  readonly filterId = signal(1);
  readonly category = signal(getDenefitsCategoryKey(1));
  readonly entityId = signal<string | null>(null);
  readonly page = signal(1);
  readonly versionType = signal(0);
  readonly search = signal('');
  readonly statusFilter = signal('0');
  readonly vipOnly = signal(false);
  readonly urgentOnly = signal(false);
  readonly orpFilter = signal(-1);
  readonly instantClaim = signal(-1);
  readonly paymentType = signal(0);
  readonly challengeType = signal(0);
  readonly startDate = signal(getVersionDateRange(0).startDate);
  readonly endDate = signal(getVersionDateRange(0).endDate);
  readonly versionDateRange = computed(() => getVersionDateRange(this.versionType()));
  readonly startDateMax = computed(() => {
    const versionEnd = this.versionDateRange().endDate;
    const selectedEnd = this.endDate();
    const cap = !selectedEnd || selectedEnd > versionEnd ? versionEnd : selectedEnd;
    return cap;
  });
  readonly totalCount = signal(0);
  readonly totalPages = signal(0);
  readonly rows = signal<any[]>([]);
  readonly dashboardSections = signal<DenefitsDashboardSection[]>([]);
  readonly balance = signal<DenefitsBalanceSnapshot>(EMPTY_DENEFITS_BALANCE);
  readonly detailSummary = signal<any | null>(null);
  readonly selectedEntity = signal<any | null>(null);
  readonly selectedIds = signal<string[]>([]);
  readonly failureRows = signal<any[]>([]);

  /** Stable per-row options cache: key = `rowId:isUrgent` → avoids new array refs each CD cycle */
  private readonly rowMenuOptionsCache = new Map<string, DropdownOption[]>();
  readonly canSelectRowBind = (row: any) => this.canSelectRow(row);

  readonly listingHeaders = computed(() =>
    changeDenefitsListingColumns(getDenefitsCategoryKey(this.filterId()))
  );

  readonly businessHeaders = computed(() =>
    changeDenefitsBusinessColumns(getDenefitsCategoryKey(this.filterId()), this.isPartnerView())
  );

  readonly detailsAllHeaders = computed(() =>
    changeDenefitsBusinessDetailsColumns(getDenefitsCategoryKey(this.filterId()))
  );

  readonly detailsContractHeaders = DEFAULT_DETAILS_CONTRACT_COLUMNS;

  readonly currentCategory = computed(
    () => getDenefitsCategoryById(this.filterId()) ?? this.visibleCategories()[0] ?? this.categories[0]
  );
  readonly statusOptions = computed(() => getDenefitsStatusOptions(this.filterId()));
  readonly categoryDropdownOptions = computed<DropdownOption[]>(() =>
    this.visibleCategories().map((option) => ({
      value: String(option.id),
      label: option.label,
    }))
  );
  readonly statusDropdownOptions = computed<DropdownOption[]>(() =>
    this.statusOptions().map((option) => ({
      value: String(option.value),
      label: option.label,
    }))
  );
  readonly viewDropdownOptions = computed<DropdownOption[]>(() => [
    { value: 'listing', label: 'All Payments' },
    { value: 'business', label: this.isPartnerView() ? 'All Partners' : 'All Businesses' },
  ]);

  readonly versionDropdownSelected = computed(() => String(this.versionType()));
  readonly filterIdDropdownSelected = computed(() => String(this.filterId()));
  readonly orpDropdownSelected = computed(() => String(this.orpFilter()));
  readonly instantClaimDropdownSelected = computed(() => String(this.instantClaim()));
  readonly paymentTypeDropdownSelected = computed(() => String(this.paymentType()));
  readonly challengeDropdownSelected = computed(() => String(this.challengeType()));
  readonly canGoPrev = computed(() => this.page() > 1);
  readonly canGoNext = computed(() => this.page() < this.totalPages());
  readonly offset = computed(() => (this.page() - 1) * this.pageSize);
  readonly isPartnerView = computed(() => isPartnerFilter(this.filterId()));
  readonly fundingOptions = computed(() => this.api.buildFundingOptions(this.balance()));
  readonly selectedRows = computed(() =>
    this.rows().filter((row) => this.selectedIds().includes(this.rowId(row)))
  );
  readonly selectedPayablePayments = computed(() =>
    this.selectedRows().filter((row) => this.isRowPayable(row))
  );
  readonly selectedPayableCount = computed(() => this.selectedPayablePayments().length);
  readonly totalSelectedAmount = computed(() =>
    this.selectedPayablePayments().reduce((sum, row) => sum + this.getPayableAmount(row), 0)
  );
  readonly selectedBusinessesCount = computed(() => {
    const ids = new Set<string>();
    for (const row of this.selectedPayablePayments()) {
      const bizId = this.isPartnerView() ? row?.partner_id : row?.doctor_id;
      if (bizId) ids.add(String(bizId));
    }
    return ids.size;
  });
  readonly selectedUrgentCount = computed(() =>
    this.selectedPayablePayments().filter((row) => this.isUrgent(row)).length
  );
  readonly selectedUrgentAmount = computed(() =>
    this.selectedPayablePayments()
      .filter((row) => this.isUrgent(row))
      .reduce((sum, row) => sum + this.getPayableAmount(row), 0)
  );
  readonly moreOptions = computed<DropdownOption[]>(() => {
    const options: DropdownOption[] = [];
    const selected = this.selectedRows();
    const urgentCount = this.selectedUrgentCount();

    if (selected.length > 0 && urgentCount < selected.length) {
      options.push({
        value: 'mark_urgent',
        label: 'Mark Urgent',
        icon: 'zap',
        iconClass: 'text-[#ad46ff]',
      });
    }

    if (selected.length > 0 && urgentCount === selected.length) {
      options.push({
        value: 'remove_urgent',
        label: 'Remove Urgent',
        icon: 'zap-off',
        iconClass: 'text-[#ad46ff]',
      });
    }

    // Schedule Payment flow disabled for Denefits v2.
    // const showSchedulePayment =
    //   !(this.view() === 'business-details' && this.detailMode() === 'contract');
    // if (selected.length > 0 && urgentCount === 0 && showSchedulePayment) {
    //   options.push({
    //     value: 'schedule_payment',
    //     label: 'Schedule Payment',
    //     icon: 'calendar',
    //     iconClass: 'text-[#1581e5]',
    //   });
    // }

    options.push({
      value: 'remove_unpaid',
      label: 'Remove From Unpaid',
      icon: 'trash2',
      iconClass: 'text-[#f59e0b]',
    });

    return options;
  });

  readonly allVisibleSelected = computed(() => {
    const selectable = this.rows().filter((row) => this.canSelectRow(row));
    return selectable.length > 0 && selectable.every((row) => this.selectedIds().includes(this.rowId(row)));
  });
  readonly isRowActionsDisabled = computed(() => this.selectedIds().length > 0);
  readonly isContractWiseMode = computed(() =>
    this.filterId() === 3 && this.detailMode() === 'contract'
  );
  readonly groupedDetailRows = computed(() => {
    if (this.isContractWiseMode()) {
      return this.rows().map((row, index) => {
        const innerRows = this.parseContractWisePayments(row);
        return {
          key: String(row?.record_id || this.rowId(row) || index),
          contractCode: this.getAccountCode(row),
          paymentTypeLabel: this.getAchArPpTypeLabel(row),
          paymentTypeClass: this.getAchArPpTypeBadgeClass(row),
          totalAmount: Number(row?.total_amount_to_pay ?? 0),
          rows: innerRows.length ? innerRows : [row],
        };
      });
    }

    const groups = new Map<string, { contractCode: string; totalAmount: number; rows: any[] }>();
    for (const row of this.rows()) {
      const contractKey = String(row?.contract_id || row?.contract_code || this.rowId(row));
      if (!groups.has(contractKey)) {
        groups.set(contractKey, {
          contractCode: String(row?.contract_code || row?.contract_id || 'Unknown Contract'),
          totalAmount: 0,
          rows: [],
        });
      }
      const group = groups.get(contractKey)!;
      group.rows.push(row);
      group.totalAmount += this.getPayableAmount(row);
    }
    return Array.from(groups.entries()).map(([key, value]) => ({
      key,
      contractCode: value.contractCode,
      paymentTypeLabel: '',
      paymentTypeClass: '',
      totalAmount: value.totalAmount,
      rows: value.rows,
    }));
  });
  readonly dashboardAllocationRows = computed(() =>
    this.dashboardSections()
      .flatMap((section) => section.rows)
      .filter((row) => Number(row?.amount ?? 0) > 0)
      .sort((left, right) => Number(right?.amount ?? 0) - Number(left?.amount ?? 0))
  );
  readonly dashboardTableSections = computed<DenefitsDashboardTableSection[]>(() =>
    this.dashboardSections().map((section) => ({
      ...section,
      headers: this.buildDashboardTableHeaders(section),
      tableRows: section.rows.map((row) => this.mapDashboardTableRow(row)),
    }))
  );
  readonly balanceAllocationRows = computed<AllocationLegendRow[]>(() => {
    if (this.isDashboard()) {
      return this.dashboardAllocationRows().map((allocation) => ({
        label: String(allocation?.label || 'Unknown'),
        amount: Number(allocation?.amount ?? 0),
        color: this.getToneColor(String(allocation?.tone || 'slate')),
      }));
    }

    return this.fundingOptions()
      .filter((option) => Number(option?.amount ?? 0) > 0)
      .map((option) => ({
        label: option.value === 'card' ? 'Card Balance' : 'Bank Balance',
        amount: Number(option?.amount ?? 0),
        color: option.value === 'bank_account' ? '#16a34a' : '#2563eb',
      }));
  });
  readonly allocationVisibleRows = computed(() => this.balanceAllocationRows().slice(0, 2));
  readonly allocationMoreCount = computed(() => Math.max(0, this.balanceAllocationRows().length - 2));
  readonly paymentAllocationAmount = computed(() =>
    this.balanceAllocationRows().reduce((sum, row) => sum + Number(row.amount ?? 0), 0)
  );
  readonly amountToPayDisplay = computed(() =>
    this.isDashboard()
      ? this.dashboardAllocationRows().reduce((sum, row) => sum + Number(row?.amount ?? 0), 0)
      : this.balance().total_amount_to_pay
  );
  readonly balanceAllocationProgressData = computed<ProgressBarData[]>(() => {
    const rows = this.balanceAllocationRows();
    if (!rows.length) return [];

    const primary = rows.slice(0, 2);
    const remainingAmount = rows.slice(2).reduce((sum, row) => sum + row.amount, 0);
    const segments = [
      ...primary.map((row) => ({ value: row.amount, color: row.color })),
      ...(remainingAmount > 0 ? [{ value: remainingAmount, color: '#4b5563' }] : []),
    ];

    return [
      {
        label: 'Payment Allocation',
        value: 100,
        color: segments[0]?.color || '#2563eb',
        segments,
      },
    ];
  });
  readonly contractBreakdownRows = computed(() => {
    const contractData = this.detailSummary()?.contract_data || {};
    return [
      { label: 'Active', value: Number(contractData?.active ?? 0), tone: 'green' },
      { label: 'Overdue', value: Number(contractData?.overdue ?? 0), tone: 'rose' },
      { label: 'Completed', value: Number(contractData?.completed ?? 0), tone: 'blue' },
      { label: 'Cancelled', value: Number(contractData?.cancelled ?? 0), tone: 'slate' },
    ];
  });
  readonly businessDetailContractStats = computed(() => {
    const contractData = this.detailSummary()?.contract_data || {};
    return [
      { label: 'Total Contracts', value: Number(contractData?.total_contracts ?? 0), tone: 'none' as const },
      { label: 'Active', value: Number(contractData?.active ?? 0), tone: 'green' as const },
      { label: 'Completed', value: Number(contractData?.completed ?? 0), tone: 'blue' as const },
      { label: 'Overdue', value: Number(contractData?.overdue ?? 0), tone: 'rose' as const },
      { label: 'Cancelled', value: Number(contractData?.cancelled ?? 0), tone: 'slate' as const },
    ];
  });

  private lastRouteSignature = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly api: DenefitsUnpaidApiService,
    private readonly unpaidPaymentsModal: UnpaidPaymentsModalService,
    private readonly calculationService: UnpaidPaymentsCalculationService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const nextState = this.parseRouteState(params);
      const signature = JSON.stringify(nextState);
      if (signature === this.lastRouteSignature) {
        return;
      }

      this.lastRouteSignature = signature;
      this.applyRouteState(nextState);
      this.refreshData();
    });
  }

  toneChipClass(tone: ReturnType<typeof normalizeToneClasses>): string {
    return tone.chip;
  }

  toneBadgeClass(tone: ReturnType<typeof normalizeToneClasses>): string {
    return tone.badge;
  }

  getCategoryTone(filterId: number): ReturnType<typeof normalizeToneClasses> {
    return normalizeToneClasses(getDenefitsCategoryById(filterId)?.tone ?? 'blue');
  }

  get viewLabel(): string {
    if (this.view() === 'business-details') return 'Business Details';
    if (this.view() === 'business') return this.isPartnerView() ? 'Partners' : 'Businesses';
    if (this.view() === 'listing') return 'Payments';
    return 'Overview';
  }

  get summaryHeading(): string {
    if (this.view() !== 'business-details') {
      return getDenefitsCategoryLabel(this.filterId());
    }

    const selected = this.selectedEntity();
    const summary = this.detailSummary();
    if (this.isPartnerView()) {
      return (
        selected?.partner_name ||
        summary?.partner_name ||
        summary?.business_owner_name ||
        getDenefitsCategoryLabel(this.filterId())
      );
    }

    return (
      selected?.business_name ||
      selected?.business_owner_name ||
      summary?.business_name ||
      summary?.business_owner_name ||
      summary?.doctor_name ||
      getDenefitsCategoryLabel(this.filterId())
    );
  }

  get upcomingAmountTooltip(): string {
    return this.calculationService.formatUpcomingAmountTooltip(this.balance().upcoming_balance ?? 0);
  }

  get summarySubtitle(): string {
    const selected = this.selectedEntity();
    const summary = this.detailSummary();
    if (this.isPartnerView()) {
      return (
        selected?.partner_name ||
        summary?.partner_name ||
        selected?.business_owner_name ||
        summary?.business_owner_name ||
        ''
      );
    }

    return (
      selected?.business_owner_name ||
      summary?.business_owner_name ||
      selected?.doctor_name ||
      summary?.doctor_name ||
      ''
    );
  }

  get businessDetailStatusLabel(): string {
    const status = String(this.detailSummary()?.multiple_active_status || '').toUpperCase();
    if (status === 'ACTIVE') return 'Active';
    if (status === 'INACTIVE') return 'Inactive';
    return '';
  }

  get versionLabel(): string {
    return getVersionLabel(this.versionType());
  }

  get canUseContractMode(): boolean {
    return Boolean(this.currentCategory().supportsContractView);
  }

  get contractTabLabel(): string {
    return this.filterId() === 3 ? 'By PP/AR/Subscription' : 'By Contract';
  }

  isDashboard(): boolean {
    return this.view() === 'dashboard';
  }

  showOrpFilters(): boolean {
    return this.filterId() === 2 && this.view() === 'listing';
  }

  showPaymentTypeFilter(): boolean {
    return this.filterId() === 3 && this.view() === 'listing';
  }

  /** Listing filters that show payable amount (and urgent badge) in the recurringAmount column. */
  showPayableAmountInRecurringColumn(): boolean {
    return this.view() === 'listing' && (this.filterId() === 3 || this.filterId() === 4);
  }

  showChallengeFilter(): boolean {
    return this.filterId() === 4 && this.view() === 'listing';
  }

  canSelectRow(row: any): boolean {
    return !!this.rowId(row) && this.getPayableAmount(row) > 0;
  }

  isRowPayable(row: any): boolean {
    if (!this.canSelectRow(row)) return false;
    return !this.calculationService.isRowTransferDisabled(row);
  }

  isSelected(row: any): boolean {
    return this.selectedIds().includes(this.rowId(row));
  }

  rowId(row: any): string {
    if (this.view() === 'business') {
      const entity = this.isPartnerView() ? row?.partner_id : row?.doctor_id;
      return entity === undefined || entity === null || entity === '' ? '' : `entity:${entity}`;
    }
    const paymentId = row?.payment_id ?? row?.contract_id ?? row?.unique_id ?? row?.unpaid_details?.id ?? row?.id;
    return paymentId === undefined || paymentId === null || paymentId === '' ? '' : `payment:${paymentId}`;
  }

  rowNumber(index: number): number {
    return this.offset() + index + 1;
  }

  toggleRow(row: any): void {
    const id = this.rowId(row);
    if (!id || !this.canSelectRow(row)) return;
    const next = [...this.selectedIds()];
    const existingIndex = next.indexOf(id);
    if (existingIndex > -1) {
      next.splice(existingIndex, 1);
    } else {
      next.push(id);
    }
    this.selectedIds.set(next);
  }

  toggleSelectAllVisible(): void {
    if (this.allVisibleSelected()) {
      this.selectedIds.set([]);
      return;
    }

    const ids = this.rows()
      .filter((row) => this.canSelectRow(row))
      .map((row) => this.rowId(row));
    this.selectedIds.set(ids);
  }

  onSelectionChange(selectedRows: any[]): void {
    const currentPageIds = this.rows().map(row => this.rowId(row)).filter(Boolean);
    const selectedCurrentPageIds = selectedRows.map(row => this.rowId(row)).filter(Boolean);
    const otherPageIds = this.selectedIds().filter(id => !currentPageIds.includes(id));
    this.selectedIds.set([...otherPageIds, ...selectedCurrentPageIds]);

    if (this.selectedIds().length > 0) {
      this.closeActionMenu();
      this.closeContractPopover();
    }
  }

  getRowMenuOptions(row: any): DropdownOption[] {
    const cacheKey = `${this.rowId(row)}:${this.isUrgent(row)}`;
    if (this.rowMenuOptionsCache.has(cacheKey)) {
      return this.rowMenuOptionsCache.get(cacheKey)!;
    }
    const options: DropdownOption[] = [];
    if (!this.isUrgent(row)) {
      options.push({
        value: 'mark_urgent',
        label: 'Mark Urgent',
        icon: 'zap',
        iconClass: 'text-[#ad46ff]',
      });
    } else {
      options.push({
        value: 'remove_urgent',
        label: 'Remove Urgent',
        icon: 'zap-off',
        iconClass: 'text-[#ad46ff]',
      });
    }
    options.push({
      value: 'notes',
      label: 'Notes',
      icon: 'clipboard-list',
      iconClass: 'text-[#1581e5]',
    });
    options.push({
      value: 'remove',
      label: 'Remove',
      icon: 'trash2',
      iconClass: 'text-red-600 dark:text-red-400',
    });
    this.rowMenuOptionsCache.set(cacheKey, options);
    return options;
  }

  hasUrgentPaymentAvailable(row: any): boolean {
    return this.getUrgentCount(row) > 0;
  }

  getBusinessRowMenuOptions(row: any): DropdownOption[] {
    if (!this.hasUrgentPaymentAvailable(row)) {
      return [];
    }

    const cacheKey = `business:${this.rowId(row)}:urgent-menu`;
    if (this.rowMenuOptionsCache.has(cacheKey)) {
      return this.rowMenuOptionsCache.get(cacheKey)!;
    }

    const options: DropdownOption[] = [
      {
        value: 'pay_urgent',
        label: 'Pay Urgent Payments',
        icon: 'zap',
        iconClass: 'text-[#ad46ff]',
      },
    ];
    this.rowMenuOptionsCache.set(cacheKey, options);
    return options;
  }

  getBusinessDetailsRowMenuOptions(row: any): DropdownOption[] {
    const cacheKey = `business-details:${this.rowId(row)}:${this.isUrgent(row)}`;
    if (this.rowMenuOptionsCache.has(cacheKey)) {
      return this.rowMenuOptionsCache.get(cacheKey)!;
    }

    const options: DropdownOption[] = [];
    if (!this.isUrgent(row)) {
      options.push({
        value: 'mark_urgent',
        label: 'Mark Urgent',
        icon: 'zap',
        iconClass: 'text-[#ad46ff]',
      });
    } else {
      options.push({
        value: 'remove_urgent',
        label: 'Remove Urgent',
        icon: 'zap-off',
        iconClass: 'text-[#ad46ff]',
      });
    }

    // Schedule Payment flow disabled for Denefits v2.
    // options.push({
    //   value: 'schedule_payment',
    //   label: 'Schedule Payment',
    //   icon: 'calendar',
    //   iconClass: 'text-[#0c66e8]',
    // });
    options.push({
      value: 'remove_unpaid',
      label: 'Remove From Unpaid',
      icon: 'trash2',
      iconClass: 'text-[#c45d00]',
    });
    options.push({
      value: 'notes',
      label: 'View Notes',
      icon: 'file-text',
      iconClass: 'text-[#7d7d93] dark:text-ink-muted',
    });

    this.rowMenuOptionsCache.set(cacheKey, options);
    return options;
  }

  getBusinessDetailsContractRowMenuOptions(row: any): DropdownOption[] {
    const cacheKey = `business-details-contract:${this.rowId(row)}:${this.isUrgent(row)}`;
    if (this.rowMenuOptionsCache.has(cacheKey)) {
      return this.rowMenuOptionsCache.get(cacheKey)!;
    }

    const options: DropdownOption[] = [];
    if (!this.isUrgent(row)) {
      options.push({
        value: 'mark_urgent',
        label: 'Mark Urgent',
        icon: 'zap',
        iconClass: 'text-[#ad46ff]',
      });
    } else {
      options.push({
        value: 'remove_urgent',
        label: 'Remove Urgent',
        icon: 'zap-off',
        iconClass: 'text-[#ad46ff]',
      });
    }
    options.push({
      value: 'remove_unpaid',
      label: 'Remove From Unpaid',
      icon: 'trash2',
      iconClass: 'text-[#c45d00]',
    });
    options.push({
      value: 'notes',
      label: 'View Notes',
      icon: 'file-text',
      iconClass: 'text-[#7d7d93] dark:text-ink-muted',
    });

    this.rowMenuOptionsCache.set(cacheKey, options);
    return options;
  }

  private readonly lendeeMenuOptions: DropdownOption[] = [
    { value: 'view_logs', label: 'View Logs', icon: 'file-text', iconClass: 'text-[#0c66e8]' },
  ];

  getDisplayRowMenuOptions(row: any): DropdownOption[] {
    if (this.isLendeeFilter()) return this.lendeeMenuOptions;
    if (this.view() === 'business') {
      return this.getBusinessRowMenuOptions(row);
    }
    if (this.view() === 'business-details') {
      return this.detailMode() === 'contract'
        ? this.getBusinessDetailsContractRowMenuOptions(row)
        : this.getBusinessDetailsRowMenuOptions(row);
    }
    return this.getRowMenuOptions(row);
  }

  onRowMenuOptionClick(value: unknown, row: any): void {
    const raw = this.coerceDropdownPrimitive(value);
    switch (raw) {
      case 'view_logs':
        this.showLendeeViewLogs(row);
        break;
      case 'pay_urgent':
        this.payBusinessUrgent(row);
        break;
      // case 'schedule_payment':
      //   toast.info('Schedule Payment functionality coming soon.');
      //   break;
      case 'remove_unpaid':
        this.removeRowFromUnpaid(row);
        break;
      case 'mark_urgent':
        this.markRowUrgent(row, true);
        break;
      case 'remove_urgent':
        this.markRowUrgent(row, false);
        break;
      case 'notes':
        this.showNotes(row);
        break;
      case 'remove':
        this.removeRowFromUnpaid(row);
        break;
    }
  }

  private getActionMenuWidth(): number {
    return this.view() === 'business' || this.view() === 'business-details' ? 220 : this.actionMenuWidth;
  }

  usesWideActionMenu(): boolean {
    return this.view() === 'business' || this.view() === 'business-details';
  }

  toggleActionMenu(row: any, event: Event): void {
    event.stopPropagation();
    if (this.isRowActionsDisabled()) return;
    this.closeContractPopover();
    const id = this.rowId(row);
    if (!id) return;
    if (this.openActionMenuRowId() === id) {
      this.closeActionMenu();
      return;
    }

    const trigger = event.currentTarget as HTMLElement;
    this.actionMenuTrigger = trigger;
    this.openActionMenuRowId.set(id);
    this.openActionMenuRow.set(row);
    this.actionMenuPosition.set(this.computeActionMenuPosition(trigger, row));

    setTimeout(() => this.refineActionMenuPosition(), 0);
  }

  closeActionMenu(): void {
    this.openActionMenuRowId.set(null);
    this.openActionMenuRow.set(null);
    this.actionMenuPosition.set(null);
    this.actionMenuTrigger = null;
  }

  openContractPopover(row: any, event: Event): void {
    if (!this.hasContractBreakdown(row)) return;

    this.cancelCloseContractPopover();
    this.closeActionMenu();

    const id = this.rowId(row);
    if (!id) return;

    const trigger = event.currentTarget as HTMLElement;
    this.contractPopoverTrigger = trigger;
    this.openContractPopoverRowId.set(id);
    this.openContractPopoverRow.set(row);
    this.contractPopoverPosition.set(
      this.computeContractPopoverPosition(trigger, this.contractPopoverEstimatedHeight)
    );

    setTimeout(() => this.refineContractPopoverPosition(), 0);
  }

  scheduleCloseContractPopover(): void {
    this.cancelCloseContractPopover();
    this.contractPopoverHideTimeout = setTimeout(() => this.closeContractPopover(), 120);
  }

  cancelCloseContractPopover(): void {
    if (this.contractPopoverHideTimeout) {
      clearTimeout(this.contractPopoverHideTimeout);
      this.contractPopoverHideTimeout = undefined;
    }
  }

  closeContractPopover(): void {
    this.cancelCloseContractPopover();
    this.openContractPopoverRowId.set(null);
    this.openContractPopoverRow.set(null);
    this.contractPopoverPosition.set(null);
    this.contractPopoverTrigger = null;
  }

  isContractPopoverOpen(row: any): boolean {
    const id = this.rowId(row);
    return !!id && this.openContractPopoverRowId() === id;
  }

  private computeContractPopoverPosition(
    trigger: HTMLElement,
    menuHeight = this.contractPopoverEstimatedHeight
  ): { top: number; left: number } {
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const bottomReserve =
      this.selectedIds().length > 0 ? this.actionMenuBottomBarReserve : 0;
    const maxBottom = viewportHeight - bottomReserve - this.actionMenuViewportMargin;
    const maxTop = this.actionMenuViewportMargin;

    const spaceBelow = maxBottom - rect.bottom;
    const spaceAbove = rect.top - maxTop;

    let top =
      spaceBelow >= menuHeight + this.contractPopoverGap || spaceBelow >= spaceAbove
        ? rect.bottom + this.contractPopoverGap
        : rect.top - menuHeight - this.contractPopoverGap;

    top = Math.max(maxTop, Math.min(top, maxBottom - menuHeight));

    let left = rect.left;
    left = Math.max(
      this.actionMenuViewportMargin,
      Math.min(left, viewportWidth - this.contractPopoverWidth - this.actionMenuViewportMargin)
    );

    return { top, left };
  }

  private refineContractPopoverPosition(): void {
    const trigger = this.contractPopoverTrigger;
    const row = this.openContractPopoverRow();
    if (!trigger || !row || !this.openContractPopoverRowId()) return;

    const measuredHeight = this.contractPopoverPanel?.nativeElement.offsetHeight;
    if (!measuredHeight) return;

    this.contractPopoverPosition.set(
      this.computeContractPopoverPosition(trigger, measuredHeight)
    );
  }

  private estimateActionMenuHeight(row: any): number {
    return this.getDisplayRowMenuOptions(row).length * this.actionMenuItemHeight + this.actionMenuPadding;
  }

  private computeActionMenuPosition(
    trigger: HTMLElement,
    row: any,
    menuHeight = this.estimateActionMenuHeight(row)
  ): { top: number; left: number } {
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const bottomReserve =
      this.selectedIds().length > 0 ? this.actionMenuBottomBarReserve : 0;
    const maxBottom = viewportHeight - bottomReserve - this.actionMenuViewportMargin;
    const maxTop = this.actionMenuViewportMargin;

    const spaceBelow = maxBottom - rect.bottom;
    const spaceAbove = rect.top - maxTop;

    let top =
      spaceBelow >= menuHeight + this.actionMenuGap || spaceBelow >= spaceAbove
        ? rect.bottom + this.actionMenuGap
        : rect.top - menuHeight - this.actionMenuGap;

    top = Math.max(maxTop, Math.min(top, maxBottom - menuHeight));

    const menuWidth = this.getActionMenuWidth();
    let left = rect.right - menuWidth;
    left = Math.max(
      this.actionMenuViewportMargin,
      Math.min(left, viewportWidth - menuWidth - this.actionMenuViewportMargin)
    );

    return { top, left };
  }

  private refineActionMenuPosition(): void {
    const trigger = this.actionMenuTrigger;
    const row = this.openActionMenuRow();
    if (!trigger || !row || !this.openActionMenuRowId()) return;

    const measuredHeight = this.actionMenuPanel?.nativeElement.offsetHeight;
    if (!measuredHeight) return;

    this.actionMenuPosition.set(
      this.computeActionMenuPosition(trigger, row, measuredHeight)
    );
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  @HostListener('document:scroll', [])
  onActionMenuViewportChange(): void {
    const trigger = this.actionMenuTrigger;
    const row = this.openActionMenuRow();
    if (trigger && row && this.openActionMenuRowId()) {
      const measuredHeight =
        this.actionMenuPanel?.nativeElement.offsetHeight ??
        this.estimateActionMenuHeight(row);
      this.actionMenuPosition.set(
        this.computeActionMenuPosition(trigger, row, measuredHeight)
      );
    }

    const contractTrigger = this.contractPopoverTrigger;
    const contractRow = this.openContractPopoverRow();
    if (contractTrigger && contractRow && this.openContractPopoverRowId()) {
      const measuredHeight =
        this.contractPopoverPanel?.nativeElement.offsetHeight ??
        this.contractPopoverEstimatedHeight;
      this.contractPopoverPosition.set(
        this.computeContractPopoverPosition(contractTrigger, measuredHeight)
      );
    }
  }

  isActionMenuOpen(row: any): boolean {
    const id = this.rowId(row);
    return !!id && this.openActionMenuRowId() === id;
  }

  onRowMenuOptionSelect(value: string | number, row: any, event: Event): void {
    event.stopPropagation();
    this.onRowMenuOptionClick(value, row);
    this.closeActionMenu();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-row-action-menu-root]') || target?.closest('[data-contract-popover-root]')) {
      return;
    }
    this.closeActionMenu();
    this.closeContractPopover();
  }

  goHome(): void {
    this.navigateWithState({ view: 'dashboard' });
  }

  onCategoryClick(): void {
    if (this.view() === 'business-details') {
      this.navigateWithState({ view: 'business', entityId: null, page: 1, detailMode: 'all' });
      return;
    }

    this.navigateWithState({ view: 'listing', entityId: null });
  }

  private coerceDropdownPrimitive(value: unknown): string | number | null {
    if (value === undefined || value === null) return null;
    const single = Array.isArray(value) ? value[0] : value;
    if (single === undefined || single === null) return null;
    return single as string | number;
  }

  onMoreOptionClick(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    switch (raw) {
      case 'mark_urgent':
        this.markSelectedUrgent(true);
        break;
      case 'remove_urgent':
        this.markSelectedUrgent(false);
        break;
      // case 'schedule_payment':
      //   toast.info('Schedule Payment functionality coming soon.');
      //   break;
      case 'remove_unpaid':
        this.removeSelectedFromUnpaid();
        break;
    }
  }

  onExportDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === 'xlsx') {
      this.exportRows(false);
    } else if (raw === 'csv') {
      this.exportRows(true);
    }
  }

  onStartDateChange(value: string): void {
    const range = this.versionDateRange();
    const clamped = this.calculationService.clampUnpaidListDateRange(value ?? '', this.endDate(), {
      min: range.startDate,
      max: range.endDate,
    });
    if (clamped.startDate === this.startDate() && clamped.endDate === this.endDate()) return;
    this.startDate.set(clamped.startDate);
    this.endDate.set(clamped.endDate);
    this.applyFilters();
  }

  onEndDateChange(value: string): void {
    const range = this.versionDateRange();
    const clamped = this.calculationService.clampUnpaidListDateRange(this.startDate(), value ?? '', {
      min: range.startDate,
      max: range.endDate,
    });
    if (clamped.startDate === this.startDate() && clamped.endDate === this.endDate()) return;
    this.startDate.set(clamped.startDate);
    this.endDate.set(clamped.endDate);
    this.applyFilters();
  }

  onVersionDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.changeVersion(Number(raw));
  }

  onCategoryDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.changeCategory(Number(raw));
  }

  onStatusDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.statusFilter.set(String(raw));
  }

  onListingViewDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw !== 'listing' && raw !== 'business') return;
    this.changeView(raw);
  }

  onOrpDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.orpFilter.set(Number(raw));
  }

  onInstantClaimDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.instantClaim.set(Number(raw));
  }

  onPaymentTypeDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.paymentType.set(Number(raw));
  }

  onChallengeDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.challengeType.set(Number(raw));
  }

  changeVersion(versionType: number): void {
    const range = getVersionDateRange(versionType);
    this.navigateWithState({
      versionType,
      startDate: range.startDate,
      endDate: range.endDate,
      page: 1,
    });
  }

  changeCategory(filterId: number): void {
    this.navigateWithState({
      view: 'listing',
      filterId,
      category: getDenefitsCategoryKey(filterId),
      entityId: null,
      detailMode: 'all',
      page: 1,
      search: '',
      statusFilter: '0',
      vipOnly: false,
      urgentOnly: false,
      orpFilter: -1,
      instantClaim: -1,
      paymentType: 0,
      challengeType: 0,
      lendeeShareType: 0,
      lendeePaymentStatus: 0,
    });
  }

  openDashboardCategory(row: any): void {
    this.changeCategory(Number(row?.unpaid_filter_id ?? 1));
  }

  onDashboardTableAction(row: any): void {
    if (Number(row?.unpaidPayments ?? 0) <= 0) {
      return;
    }
    this.openDashboardCategory(row);
  }

  buildDashboardTableHeaders(section: DenefitsDashboardSection): TableHeader[] {
    const isPartnerSection = section.title.toLowerCase().includes('partner');
    return changeDenefitsDashboardColumns(isPartnerSection);
  }

  private mapDashboardTableRow(row: DenefitsDashboardRow): Record<string, unknown> {
    const display = getDenefitsCategoryDisplay(row.category);
    const unpaidMuted = row.unpaidPayments === 0;
    const amountMuted = row.amount === 0;
    const actionDisabled = row.unpaidPayments === 0;
    const actionBtnClass = actionDisabled
      ? 'inline-flex h-[37px] min-w-[150px] items-center justify-center rounded-[10px] bg-[#ad46ff] px-3 text-[14px] font-medium text-white opacity-60 cursor-not-allowed'
      : 'inline-flex h-[37px] min-w-[150px] items-center justify-center rounded-[10px] bg-[#ad46ff] px-3 text-[14px] font-medium text-white transition hover:bg-[#9a2ef0]';

    return {
      ...row,
      icon: display.icon,
      iconBgClass: display.iconBgClass,
      iconClass: display.iconClass,
      paymentCategory: this.sanitizer.bypassSecurityTrustHtml(`
        <div class="flex items-center gap-3">
          <span class="text-[15px] font-medium text-[#131921] dark:text-white">${escapeHtml(row.label)}</span>
        </div>
      `),
      unpaidPayments: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="text-[15px] font-medium ${unpaidMuted ? 'text-[#797f87]' : 'text-[#131921] dark:text-ink-soft'}">${escapeHtml(row.unpaidPayments)}</span>`
      ),
      urgentPayments: '', // Handled by table template case
      amount: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="text-[15px] ${amountMuted ? 'font-medium text-[#797f87]' : 'font-bold text-[#131921] dark:text-white'}">${this.formatDashboardAmount(row.amount)}</span>`
      ),
      action: this.sanitizer.bypassSecurityTrustHtml(`<span class="${actionBtnClass}">View Payments</span>`),
    };
  }

  private formatDashboardAmount(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  changeView(nextView: 'listing' | 'business'): void {
    if (this.view() === nextView) return;
    this.navigateWithState({
      view: nextView,
      page: 1,
      entityId: null,
      detailMode: 'all',
    });
  }

  setDetailMode(mode: DenefitsDetailMode): void {
    if (this.detailMode() === mode) return;
    this.navigateWithState({ detailMode: mode, page: 1 });
  }

  openBusinessDetails(row: any): void {
    const entityId = this.isPartnerView() ? row?.partner_id : row?.doctor_id;
    if (entityId === undefined || entityId === null || entityId === '') {
      toast.error('Unable to open business details for the selected row.');
      return;
    }

    this.selectedEntity.set(row);
    this.navigateWithState({
      view: 'business-details',
      entityId: String(entityId),
      detailMode: this.canUseContractMode ? 'all' : 'all',
      page: 1,
    });
  }

  applyFilters(): void {
    this.navigateWithState({
      page: 1,
      search: this.search().trim(),
      statusFilter: this.statusFilter(),
      vipOnly: this.vipOnly(),
      urgentOnly: this.urgentOnly(),
      orpFilter: this.orpFilter(),
      instantClaim: this.instantClaim(),
      paymentType: this.paymentType(),
      challengeType: this.challengeType(),
      startDate: this.startDate(),
      endDate: this.endDate(),
    });
  }

  resetFilters(): void {
    const range = getVersionDateRange(this.versionType());
    this.navigateWithState({
      page: 1,
      search: '',
      statusFilter: '0',
      vipOnly: false,
      urgentOnly: false,
      orpFilter: -1,
      instantClaim: -1,
      paymentType: 0,
      challengeType: 0,
      startDate: range.startDate,
      endDate: range.endDate,
    });
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.navigateWithState({ page });
  }

  paySelected(): void {
    const rows = this.selectedPayablePayments();
    if (!rows.length) {
      toast.info('Select at least one payable row before paying.');
      return;
    }

    const total = rows.reduce((sum, row) => sum + this.getPayableAmount(row), 0);
    const transferOnly = this.shouldUseTransferFromModal();
    this.openPayDialog(
      rows,
      total,
      `Pay ${rows.length} selected item${rows.length > 1 ? 's' : ''}`,
      false,
      transferOnly ? 'Transfer From' : undefined,
      transferOnly
    );
  }

  payAllVisible(): void {
    const rows = this.rows().filter((row) => this.isRowPayable(row));
    if (!rows.length) {
      toast.info('There are no payable rows in the current view.');
      return;
    }

    const total = rows.reduce((sum, row) => sum + this.getPayableAmount(row), 0);
    const confirmTitle =
      this.view() === 'business'
        ? 'Confirm payment to all visible businesses for unpaid payments.'
        : undefined;
    this.openPayDialog(rows, total, `Pay all ${rows.length} visible item${rows.length > 1 ? 's' : ''}`, false, confirmTitle);
  }

  payUrgentPayments(): void {
    const total = this.balance().urgent_balance;
    if (this.balance().total_urgent_payments === 0 || total <= 0) {
      return;
    }

    this.openPayDialog([], total, 'Pay all urgent payments', true, 'Transfer From', true);
  }

  paySingle(row: any): void {
    if (this.isLendeeFilter()) {
      this.payLendeeSingle(row);
      return;
    }

    if (!this.isRowPayable(row)) {
      toast.info('This row is not eligible for payment.');
      return;
    }

    if (this.view() === 'business') {
      this.payBusinessAll(row);
      return;
    }

    const total = this.getPayableAmount(row);
    const transferOnly = this.shouldUseTransferFromModal();
    this.openPayDialog(
      [row],
      total,
      'Pay this unpaid item',
      false,
      transferOnly ? 'Transfer From' : undefined,
      transferOnly
    );
  }

  payLendeeSingle(row: any): void {
    if (this.isLendeePaid(row)) {
      toast.info('This lendee payment is already paid.');
      return;
    }

    const paymentId = row?.unpaid_details?.id ?? row?.id;
    const paymentTo = Number(row?.unpaid_details?.share_type ?? row?.share_type ?? 0);
    const total = this.getLendeePayableAmount(row);
    if (!paymentId || !paymentTo || total <= 0) {
      toast.info('This lendee row is not eligible for payment.');
      return;
    }

    const sources = this.fundingOptions().map((option) => ({
      id: option.value,
      label: option.value === 'card' ? 'Card' : 'Bank',
      availableAmount: option.amount,
    }));

    if (!sources.length || sources.every((source) => source.availableAmount <= 0)) {
      toast.error('No funding sources are available for this transfer.');
      return;
    }

    this.unpaidPaymentsModal
      .openTransferFrom({
        totalAmount: total,
        sources,
        title: 'Transfer From',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((transferResult) => {
        if (!transferResult?.confirmed) return;

        const available = this.balance().available_balance_by_source[transferResult.source] ?? 0;
        if (available < total) {
          toast.error(
            `Insufficient ${transferResult.source === 'card' ? 'card' : 'bank'} balance. Available: $${available.toFixed(2)}.`
          );
          return;
        }

        this.unpaidPaymentsModal
          .openConfirmPayment({
            title: 'Confirm lendee payment',
            totalAmount: total,
            confirmLabel: 'Yes',
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((confirmResult) => {
            if (!confirmResult?.confirmed) return;
            this.submitLendeePayment(row, total, transferResult.source);
          });
      });
  }

  private shouldUseTransferFromModal(): boolean {
    return this.view() === 'listing' || this.view() === 'business-details';
  }

  payBusinessAll(row: any): void {
    if (!this.canSelectRow(row)) {
      toast.info('This business is not eligible for payment.');
      return;
    }

    const total = this.getBusinessSummaryAmount(row);
    const businessName = this.getPrimaryName(row);

    this.dialog
      .open(ConfirmationModalComponent, {
        width: '420px',
        data: {
          title: 'Confirm Pay All',
          description: `Are you sure you want to pay all unpaid payments for ${businessName || 'this business'}?`,
          confirmLabel: 'Pay All',
          cancelLabel: 'Cancel',
          variant: 'primary',
        } as ConfirmationModalData,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.openPayDialog(
          [row],
          total,
          'Pay all payments for this business',
          false,
          'Transfer From',
          true
        );
      });
  }

  payBusinessUrgent(row: any): void {
    if (!this.hasUrgentPaymentAvailable(row)) {
      toast.info('This business does not have any urgent payments.');
      return;
    }

    const total = Number(row?.urgent_payment_amount ?? 0);
    if (total <= 0) {
      toast.info('This business does not have any urgent payment amount to pay.');
      return;
    }

    this.openPayDialog(
      [row],
      total,
      'Pay urgent payments for this business',
      true,
      'Transfer From',
      true
    );
  }

  markSelectedUrgent(urgent: boolean): void {
    const rows = this.selectedRows();
    if (!rows.length) {
      toast.info(`Select at least one row to ${urgent ? 'mark as urgent' : 'remove from urgent'}.`);
      return;
    }
    this.openUrgentDialog(rows, urgent);
  }

  markRowUrgent(row: any, urgent: boolean): void {
    this.openUrgentDialog([row], urgent);
  }

  removeSelectedFromUnpaid(): void {
    const rows = this.selectedRows();
    if (!rows.length) {
      toast.info('Select at least one row to remove from unpaid.');
      return;
    }
    this.openRemoveDialog(rows);
  }

  removeRowFromUnpaid(row: any): void {
    this.openRemoveDialog([row]);
  }

  showNotes(row: any): void {
    this.unpaidPaymentsModal.openNotes({
      title: 'Payment Notes',
      notes: this.api.parseRawNotes(row?.payout_blocked_note),
      emptyMessage: 'No payout notes are available for this record.',
    });
  }

  exportRows(asCsv: boolean): void {
    if (this.exporting()) return;

    this.exporting.set(true);
    this.api
      .listPayments(this.buildListRequest(true))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.exporting.set(false);
          const exportedRows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : this.rows();
          const normalized = Array.isArray(exportedRows) ? exportedRows : [];
          const exportRows = this.buildExportRows(normalized);
          if (!exportRows.length) {
            toast.info('There is no data to export for the current filters.');
            return;
          }

          const sheet = XLSX.utils.json_to_sheet(exportRows);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, sheet, this.viewLabel);
          XLSX.writeFile(
            workbook,
            asCsv ? 'denefits_unpaid_payments.csv' : 'denefits_unpaid_payments.xlsx'
          );
          toast.success('Export file generated successfully.');
        },
        error: (error: Error) => {
          this.exporting.set(false);
          toast.error(error.message || 'Unable to export unpaid rows.');
        },
      });
  }

  getPrimaryName(row: any): string {
    if (this.view() === 'business') {
      if (this.isPartnerView()) {
        return row?.partner_name || row?.business_owner_name || '-';
      }
      return row?.business_name || row?.business_owner_name || '-';
    }

    return row?.business_name || row?.business_owner_name || row?.doctor_name || '-';
  }

  getSecondaryName(row: any): string {
    if (this.isPartnerView()) {
      return row?.partner_name || '-';
    }
    return row?.business_owner_name || row?.doctor_name || '-';
  }

  getEmail(row: any): string {
    return row?.doctor_email || row?.email || '-';
  }

  getPartnerEmail(row: any): string {
    return row?.partner_email || '-';
  }

  getStatusLabel(row: any): string {
    if (this.view() === 'business' && this.isPartnerView()) {
      return row?.partner_status || row?.is_partner_active || '-';
    }

    const status = row?.doctor_status || row?.business_status || row?.multiple_active_status || row?.partner_status || '-';
    if (status === 'Inactive' && Number(row?.last_active_days) > 0) {
      return `${status} (${row.last_active_days} days)`;
    }
    return status;
  }

  parseContractWisePayments(row: any): any[] {
    let raw = row?.payments_data;
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    return Array.isArray(raw) ? raw : [];
  }

  getAccountCode(row: any): string {
    return (
      row?.record_code ||
      row?.contract_code ||
      row?.subscription_payment_process_code ||
      row?.payment_process_code ||
      row?.unique_code ||
      row?.payment_id ||
      '-'
    );
  }

  showsContractStatusPill(): boolean {
    return shouldShowContractStatusPill(this.filterId());
  }

  getContractStatusLabel(row: any): string {
    const status = row?.pf_status ?? row?.contract_details?.pf_status;
    if (status == null || status === '') {
      return '';
    }

    if (typeof status === 'string') {
      const normalized = status.trim();
      if (!normalized || normalized === '-') {
        return '';
      }

      const lower = normalized.toLowerCase();
      if (lower === 'complete' || lower === 'completed') return 'Completed';
      if (lower === 'cancelled' || lower === 'canceled') return 'Cancelled';
      if (lower === 'active') return 'Active';
      if (lower === 'overdue') return 'Overdue';
      if (lower === 'initiated') return 'Initiated';
      if (lower === 'inactive') return 'Inactive';
      return normalized;
    }

    const numericStatus = Number(status);
    if (numericStatus === 1 || numericStatus === 2) return 'Active';
    if (numericStatus === 3) return 'Overdue';
    if (numericStatus === 4) return 'Completed';
    if (numericStatus === 5) return 'Cancelled';
    return '';
  }

  getContractStatusPillClass(row: any): string {
    switch (this.getContractStatusLabel(row)) {
      case 'Active':
        return 'bg-[#15b268] text-white';
      case 'Overdue':
        return 'bg-[#ffa514] text-white';
      case 'Completed':
        return 'bg-[#0c66e8] text-white';
      case 'Cancelled':
      case 'Inactive':
        return 'bg-[#f05050] text-white';
      case 'Initiated':
        return 'bg-[#7d7d93] text-white';
      default:
        return 'bg-[#f2f5f8] text-[#38385b] dark:bg-surface-3 dark:text-ink-soft';
    }
  }

  getReceivedDate(row: any): string {
    return this.calculationService.formatUnpaidTableDate(row?.received_date || row?.date_added || row?.last_payment_date || row?.last_contract_date);
  }

  getPayableAmount(row: any): number {
    return Number(
      row?.amount_to_pay ??
        row?.total_amount_to_pay ??
        row?.pay_to_partner ??
        row?.pay_to_doctor ??
        row?.pay_to_user ??
        row?.amount ??
        row?.amount_deducted ??
        0
    );
  }

  isUrgent(row: any): boolean {
    const value = row?.is_urgent;
    return value === 1 || value === '1' || value === true;
  }

  getDeductedAmount(row: any): number {
    return Number(row?.amount_deducted ?? row?.recurring_amount ?? row?.total_amount_to_pay ?? row?.amount ?? 0);
  }

  getRewardTypeLabel(row: any): string {
    const rewardType = Number(row?.business_reward_type);
    if (rewardType === 2) return '$100 Challenge';
    if (rewardType === 1) return '$15 Challenge';
    return '-';
  }

  getAchArPpTypeLabel(row: any): string {
    const sourceType = row?.source_type;
    if (sourceType) {
      const normalized = String(sourceType).toUpperCase();
      if (normalized === 'PP' || normalized === 'PAYMENT_PROCESSED') return 'Payment Processed';
      if (normalized === 'SUBS' || normalized === 'SUBSCRIPTION') return 'Subscription Payment';
      if (normalized === 'AR' || normalized === 'ACCOUNTS_RECEIVABLE') return 'Accounts Receivable';
    }
    const paymentType = Number(row?.payment_type);
    if (paymentType === 46) return 'Payment Processed';
    if (paymentType === 67) return 'Subscription Payment';
    if (paymentType === 47) return 'Accounts Receivable';
    return '-';
  }

  getAchArPpTypeClass(row: any): string {
    const paymentType = Number(row?.payment_type);
    if (paymentType === 46) return 'text-[#0c66e8]';
    if (paymentType === 67) return 'text-[#ad46ff]';
    if (paymentType === 47) return 'text-[#15b268]';
    return 'text-[#141437] dark:text-white';
  }

  getAchArPpTypeBadgeClass(row: any): string {
    const label = this.getAchArPpTypeLabel(row);
    if (label === 'Payment Processed') return 'bg-[#eef5ff] text-[#0c66e8] dark:bg-blue-500/15 dark:text-[#9cc5ff]';
    if (label === 'Subscription Payment') return 'bg-[#f4e5ff] text-[#ad46ff] dark:bg-purple-800 dark:text-[#e0baff]';
    if (label === 'Accounts Receivable') return 'bg-[#edfff3] text-[#15b268] dark:bg-[#173b31] dark:text-[#7ae5ac]';
    return 'bg-[#f2f5f8] text-[#7d7d93] dark:bg-surface-3 dark:text-ink-muted';
  }

  getOrpLabel(row: any): string {
    const value = row?.is_orp;
    if (value === 1 || value === '1') return 'Yes';
    if (value === 0 || value === '0') return 'No';
    return value != null && value !== '' ? String(value) : '-';
  }

  getInstantClaimLabel(row: any): string {
    const value = row?.is_orp_instant_claim;
    if (value === 1 || value === '1') return 'Yes';
    if (value === 0 || value === '0') return 'No';
    return value != null && value !== '' ? String(value) : '-';
  }

  getLastContractDate(row: any): string {
    return this.calculationService.formatUnpaidTableDate(row?.last_contract_date);
  }

  getBusinessSummaryAmount(row: any): number {
    return Number(row?.amount_to_pay ?? row?.urgent_payment_amount ?? 0);
  }

  getUrgentCount(row: any): number {
    return Number(row?.urgent_payment_count ?? (this.isUrgent(row) ? 1 : 0));
  }

  getUrgentPaymentAmount(row: any): number {
    return Number(row?.urgent_payment_amount ?? 0);
  }

  isBusinessVip(row: any): boolean {
    return Number(row?.is_vip) === 1;
  }

  getRowContractData(row: any): {
    active: number;
    overdue: number;
    completed: number;
    cancelled: number;
    total_contracts: number;
    last_contract_date?: string | null;
  } | null {
    const raw = row?.contract_data ?? row?.contract_summary;
    let parsed: Record<string, unknown> | null = null;

    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
    } else if (raw && typeof raw === 'object') {
      parsed = raw as Record<string, unknown>;
    }

    const totalContracts = Number(parsed?.['total_contracts'] ?? row?.total_contracts ?? row?.total_payments ?? 0);
    const hasBreakdown =
      !!parsed ||
      row?.active_count != null ||
      row?.overdue_count != null ||
      row?.completed_count != null ||
      row?.cancelled_count != null;

    if (!hasBreakdown) {
      // if (totalContracts > 0) {
      //   console.warn(`[Denefits] Row for business "${row?.business_name || ''}" has ${totalContracts} contract(s) but is missing detailed contract breakdown fields (e.g. active_count).`);
      // }
      return null;
    }

    return {
      active: Number(parsed?.['active'] ?? row?.active_count ?? 0),
      overdue: Number(parsed?.['overdue'] ?? row?.overdue_count ?? 0),
      completed: Number(parsed?.['completed'] ?? row?.completed_count ?? 0),
      cancelled: Number(parsed?.['cancelled'] ?? row?.cancelled_count ?? 0),
      total_contracts: totalContracts,
      last_contract_date: (parsed?.['last_contract_date'] ?? row?.last_contract_date ?? null) as string | null,
    };
  }

  getTotalContracts(row: any): number {
    return this.getRowContractData(row)?.total_contracts ?? Number(row?.total_contracts ?? row?.total_payments ?? 0);
  }

  getRowContractBreakdownRows(row: any): Array<{ label: string; value: number; color: string }> {
    const data = this.getRowContractData(row);
    if (!data) {
      return [];
    }

    return [
      { label: 'Active', value: data.active, color: '#1581E5' },
      { label: 'Overdue', value: data.overdue, color: '#D50000' },
      { label: 'Completed', value: data.completed, color: '#15B268' },
      { label: 'Cancelled', value: data.cancelled, color: '#7D7D93' },
    ];
  }

  formatContractLastDate(row: any): string {
    const raw = this.getRowContractData(row)?.last_contract_date;
    if (!raw) {
      return '—';
    }

    try {
      return formatDate(raw, 'MM/dd/yyyy', 'en-US');
    } catch {
      return String(raw);
    }
  }

  hasContractBreakdown(row: any): boolean {
    return this.getRowContractBreakdownRows(row).length > 0;
  }

  getPaymentDate(row: any): string {
    return this.calculationService.formatUnpaidTableDate(
      row?.payment_date ||
        row?.subscription_payment_date ||
        row?.scheduled_date ||
        row?.date_of_payment ||
        row?.date_added
    );
  }

  getStatusTone(status: string | null | undefined): 'success' | 'danger' | 'warning' | 'neutral' {
    const normalized = String(status || '')
      .trim()
      .toLowerCase();

    if (!normalized) {
      return 'neutral';
    }

    if ((normalized.includes('active') && !normalized.includes('inactive')) || normalized.includes('connected')) {
      return 'success';
    }

    if (normalized.includes('inactive') || normalized.includes('overdue') || normalized.includes('cancel')) {
      return 'danger';
    }

    if (normalized.includes('vip') || normalized.includes('urgent')) {
      return 'warning';
    }

    return 'neutral';
  }

  getStatusBadgeClass(status: string | null | undefined): string {
    switch (this.getStatusTone(status)) {
      case 'success':
        return 'bg-[#edfff3] text-[#15b268] dark:bg-[#173b31] dark:text-[#7ae5ac]';
      case 'danger':
        return 'bg-[#fff0f0] text-[#f05050] dark:bg-[#3f1d29] dark:text-[#ff9090]';
      case 'warning':
        return 'bg-[#fff9e8] text-[#ffa514] dark:bg-[#4b3817] dark:text-[#ffd37a]';
      default:
        return 'bg-[#f2f5f8] text-[#7d7d93] dark:bg-surface-3 dark:text-ink-muted';
    }
  }

  getQuickToggleClass(active: boolean, tone: 'blue' | 'green' | 'rose' | 'violet' | 'slate' = 'slate'): string {
    if (!active) {
      return 'border border-[#dfe5ed] bg-white text-[#7d7d93] hover:bg-[#f8f8f8] dark:border-surface-3 dark:bg-surface-1 dark:text-ink-muted dark:hover:bg-surface-3';
    }

    switch (tone) {
      case 'green':
        return 'border border-[#cdeedc] bg-[#edfff3] text-[#15b268] dark:border-[#235440] dark:bg-[#173b31] dark:text-[#7ae5ac]';
      case 'rose':
        return 'border border-[#ffd9d9] bg-[#fff0f0] text-[#f05050] dark:border-[#563040] dark:bg-[#3f1d29] dark:text-[#ff9090]';
      case 'violet':
        return 'border border-[#ead4ff] bg-[#f4e5ff] text-[#ad46ff] dark:border-purple-500/40 dark:bg-purple-800 dark:text-white';
      case 'blue':
        return 'border border-[#cfe3ff] bg-[#eef5ff] text-[#0c66e8] dark:border-blue-500/40 dark:bg-blue-500/15 dark:text-[#9cc5ff]';
      case 'slate':
      default:
        return 'border border-[#dfe5ed] bg-[#f2f5f8] text-[#38385b] dark:border-line dark:bg-surface-3 dark:text-ink-soft';
    }
  }

  setQuickStatus(status: string): void {
    this.statusFilter.set(this.statusFilter() === status && status !== '0' ? '0' : status);
    this.applyFilters();
  }

  toggleQuickVip(): void {
    this.vipOnly.set(!this.vipOnly());
    this.applyFilters();
  }

  toggleQuickUrgent(): void {
    this.setUrgentOnly(!this.urgentOnly());
  }

  setUrgentOnly(checked: boolean): void {
    if (this.urgentOnly() === checked) return;
    this.urgentOnly.set(checked);
    this.applyFilters();
  }

  private getToneColor(tone: string): string {
    switch (tone) {
      case 'blue':
        return '#2563eb';
      case 'green':
        return '#16a34a';
      case 'amber':
        return '#f59e0b';
      case 'violet':
        return '#a855f7';
      case 'rose':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  }

  private refreshData(): void {
    this.selectedIds.set([]);
    this.failureRows.set([]);
    this.loadBalance();
    this.loadDashboardData();

    if (this.view() === 'dashboard') {
      this.rows.set([]);
      this.detailSummary.set(null);
      this.selectedEntity.set(null);
      return;
    }

    this.loadRows();
  }

  private loadDashboardData(): void {
    const isDashboardView = this.view() === 'dashboard';
    if (isDashboardView) {
      this.dashboardLoading.set(true);
    }

    this.api
      .getDashboard(this.startDate(), this.endDate())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (!this.accessService.evaluateResponse(this.productKey, res)) {
            if (isDashboardView) {
              this.dashboardLoading.set(false);
              this.dashboardSections.set([]);
            }
            this.visibleCategories.set([]);
            this.accessService.redirectToOverview(this.router, this.route);
            return;
          }
          if (isDashboardView) {
            this.dashboardLoading.set(false);
            this.dashboardSections.set(this.api.mapDashboardSections(res));
          }
          this.visibleCategories.set(this.api.mapVisibleCategories(res));
          this.balance.update((current) => this.api.mergeBalance(current, res?.data?.balance_obj));
        },
        error: (error: any) => {
          if (this.accessService.isAccessDenied(error)) {
            if (isDashboardView) {
              this.dashboardSections.set([]);
            }
            this.visibleCategories.set([]);
            this.accessService.handleAccessDenied(this.productKey, this.router, this.route);
            return;
          }
          if (isDashboardView) {
          }
          this.visibleCategories.set([]);
          if (isDashboardView) {
            toast.error(error.message || 'Unable to load the unpaid dashboard.');
          }
        },
      });
  }

  private loadBalance(): void {
    this.balanceLoading.set(true);
    this.api
      .getBalance()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (!this.accessService.evaluateResponse(this.productKey, res)) {
            this.balanceLoading.set(false);
            this.accessService.redirectToOverview(this.router, this.route);
            return;
          }
          this.balanceLoading.set(false);
          this.balance.update((current) => this.api.mergeBalance(current, res?.data?.balance_obj));
        },
        error: (error: unknown) => {
          this.balanceLoading.set(false);
          if (this.accessService.isAccessDenied(error)) {
            this.accessService.handleAccessDenied(this.productKey, this.router, this.route);
            return;
          }
        },
      });
  }

  private loadRows(): void {
    this.loading.set(true);
    this.rows.set([]);
    this.detailSummary.set(null);

    if (this.isLendeeFilter()) {
      this.loadLendeeRows();
      return;
    }

    this.api
      .listPayments(this.buildListRequest())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (!this.accessService.evaluateResponse(this.productKey, res)) {
            this.loading.set(false);
            this.rows.set([]);
            this.totalCount.set(0);
            this.totalPages.set(0);
            this.detailSummary.set(null);
            this.accessService.redirectToOverview(this.router, this.route);
            return;
          }
          this.loading.set(false);
          const dataRows = Array.isArray(res?.data) ? res.data : [];
          this.rows.set(dataRows);
          this.totalCount.set(Number(res?.total_records ?? dataRows.length));
          this.totalPages.set(this.calculationService.resolveApiPageCount(res, this.pageSize));
          const clampedPage = this.calculationService.clampPageToApiTotal(this.page(), this.totalPages());
          if (clampedPage != null) {
            this.navigateWithState({ page: clampedPage });
            return;
          }

          if (this.view() === 'business-details') {
            const summary = this.isPartnerView() ? res?.partner_summary : res?.doctor_summary;
            this.detailSummary.set(this.api.parseSummary(summary));
          } else {
            this.detailSummary.set(null);
            if (this.view() !== 'business') {
              this.selectedEntity.set(null);
            }
          }

          this.balance.update((current) => this.api.mergeBalance(current, res?.balance_obj));
        },
        error: (error: any) => {
          this.loading.set(false);
          if (this.accessService.isAccessDenied(error)) {
            this.rows.set([]);
            this.totalCount.set(0);
            this.totalPages.set(0);
            this.detailSummary.set(null);
            this.accessService.handleAccessDenied(this.productKey, this.router, this.route);
            return;
          }
          this.rows.set([]);
          this.totalCount.set(0);
          this.totalPages.set(0);
          this.detailSummary.set(null);
          toast.error(error.message || 'Unable to load unpaid rows.');
        },
      });
  }

  private loadLendeeRows(): void {
    this.api
      .listLendeePayments({
        limit: this.pageSize,
        offset: this.offset(),
        shareType: this.lendeeShareType(),
        paymentStatus: this.lendeePaymentStatus(),
        search: this.search(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (!this.accessService.evaluateResponse(this.productKey, res)) {
            this.loading.set(false);
            this.rows.set([]);
            this.totalCount.set(0);
            this.totalPages.set(0);
            this.accessService.redirectToOverview(this.router, this.route);
            return;
          }
          const body = res?.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res;
          this.loading.set(false);
          const dataRows =
            Array.isArray(body?.records) ? body.records
            : Array.isArray(body?.data) ? body.data
            : Array.isArray(body?.unpaidPayments) ? body.unpaidPayments
            : Array.isArray(body?.payments) ? body.payments
            : Array.isArray(body) ? body
            : [];
          this.rows.set(dataRows);
          this.totalCount.set(
            Number(body?.number_of_records ?? body?.pagination?.total ?? body?.total_records ?? body?.count ?? dataRows.length)
          );
          this.totalPages.set(
            Number(body?.number_of_pages ?? body?.pagination?.total_pages ?? 0) || this.calculationService.resolveApiPageCount(body, this.pageSize)
          );
          const clampedPage = this.calculationService.clampPageToApiTotal(this.page(), this.totalPages());
          if (clampedPage != null) {
            this.navigateWithState({ page: clampedPage });
            return;
          }
          this.balance.update((current) => this.api.mergeBalance(current, body?.balance_obj ?? res?.balance_obj));
        },
        error: (error: any) => {
          this.loading.set(false);
          if (this.accessService.isAccessDenied(error)) {
            this.rows.set([]);
            this.totalCount.set(0);
            this.totalPages.set(0);
            this.accessService.handleAccessDenied(this.productKey, this.router, this.route);
            return;
          }
          this.rows.set([]);
          this.totalCount.set(0);
          this.totalPages.set(0);
          toast.error(error.message || 'Unable to load lendee unpaid payments.');
        },
      });
  }

  private buildListRequest(isExport = false): DenefitsListRequest {
    return {
      view: this.view(),
      filterId: this.filterId(),
      limit: this.pageSize,
      offset: this.offset(),
      startDate: this.startDate(),
      endDate: this.endDate(),
      search: this.search(),
      statusFilter: this.statusFilter(),
      vipOnly: this.vipOnly(),
      urgentOnly: this.urgentOnly(),
      orpFilter: this.orpFilter(),
      instantClaim: this.instantClaim(),
      paymentType: this.paymentType(),
      challengeType: this.challengeType(),
      entityId: this.entityId(),
      isExport,
      isContractWise: this.view() === 'business-details' && this.detailMode() === 'contract',
    };
  }

  private resolvePaymentConfirmTitle(rows: any[], urgentOnly: boolean): string {
    if (urgentOnly) {
      return 'Urgent Payment';
    }

    if (this.view() === 'business') {
      if (rows.length === 1) {
        return 'Confirm payment to this business';
      }
      if (rows.length > 1) {
        return 'Confirm payment to businesses for all selected unpaid payments.';
      }
    }

    if (rows.length === 1) {
      return 'Confirm payment';
    }

    return 'Confirm payment for selected unpaid payments.';
  }

  private openPayDialog(
    rows: any[],
    total: number,
    _actionLabel: string,
    urgentOnly = false,
    confirmTitle?: string,
    transferOnly = false
  ): void {
    const sources = this.fundingOptions().map((option) => ({
      id: option.value,
      label: option.value === 'card' ? 'Card' : 'Bank',
      availableAmount: option.amount,
    }));

    if (!sources.length || sources.every((source) => source.availableAmount <= 0)) {
      toast.error('No funding sources are available for this transfer.');
      return;
    }

    const openTransferFromModal = () => {
      this.unpaidPaymentsModal
        .openTransferFrom({
          totalAmount: total,
          sources,
          title: confirmTitle ?? 'Transfer From',
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((transferResult) => {
          if (!transferResult?.confirmed) return;

          const available = this.balance().available_balance_by_source[transferResult.source] ?? 0;
          if (available < total) {
            toast.error(
              `Insufficient ${transferResult.source === 'card' ? 'card' : 'bank'} balance. Available: $${available.toFixed(2)}.`
            );
            return;
          }

          this.submitPayment(
            {
              filterId: this.filterId(),
              view: this.view(),
              detailMode: this.detailMode(),
              rows,
              transferSource: transferResult.source,
              urgentOnly,
              startDate: this.startDate(),
              endDate: this.endDate(),
            },
            total
          );
        });
    };

    if (transferOnly) {
      openTransferFromModal();
      return;
    }

    this.unpaidPaymentsModal
      .openConfirmPayment({
        title: confirmTitle ?? this.resolvePaymentConfirmTitle(rows, urgentOnly),
        totalAmount: total,
        confirmLabel: 'Yes',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmResult) => {
        if (!confirmResult?.confirmed) return;
        openTransferFromModal();
      });
  }

  private submitPayment(request: DenefitsPayRequest, paidAmount: number): void {
    this.submitting.set(true);
    this.failureRows.set([]);
    this.api
      .payPayments(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.submitting.set(false);
          if (!result.success) {
            const failures = Array.isArray(result.failures) ? result.failures : [];
            this.failureRows.set(failures);
            const subtitle = failures
              .map((failure, index) =>
                `${index + 1}. ${failure?.error_message || failure?.message || JSON.stringify(failure)}`
              )
              .join('\n');
            this.unpaidPaymentsModal
              .openPaymentError({
                title: 'Payment Failed',
                statusMessage: result.message || 'Unable to complete the payment.',
                subtitle: subtitle || undefined,
                amount: paidAmount,
                confirmLabel: 'Done',
              })
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe();
            return;
          }

          this.unpaidPaymentsModal
            .openPaymentStatus({
              amount: paidAmount,
              statusMessage: 'Paid Successfully',
              subtitle: `Total Amount: ${this.formatCurrency(paidAmount)}`,
              confirmLabel: 'Done',
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.refreshData());
        },
        error: (error: any) => {
          this.submitting.set(false);
          this.unpaidPaymentsModal
            .openPaymentError({
              title: 'Payment Failed',
              statusMessage: error?.message || 'Unable to transfer payments.',
              confirmLabel: 'Done',
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        },
      });
  }

  private submitLendeePayment(row: any, paidAmount: number, transferSource: 'card' | 'bank_account'): void {
    const paymentId = row?.unpaid_details?.id ?? row?.id;
    const paymentTo = Number(row?.unpaid_details?.share_type ?? row?.share_type ?? 0);
    const paymentBy = transferSource === 'bank_account' ? 1 : 2;

    this.submitting.set(true);
    this.api
      .payLendeeMoney({
        paymentId,
        paymentTo,
        paymentBy,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.submitting.set(false);
          this.unpaidPaymentsModal
            .openPaymentStatus({
              amount: paidAmount,
              statusMessage: result.message || 'Paid Successfully',
              subtitle: `Total Amount: ${this.formatCurrency(paidAmount)}`,
              confirmLabel: 'Done',
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.refreshData());
        },
        error: (error: any) => {
          this.submitting.set(false);
          this.unpaidPaymentsModal
            .openPaymentError({
              title: 'Payment Failed',
              statusMessage: error?.message || 'Unable to complete the lendee payment.',
              amount: paidAmount,
              confirmLabel: 'Done',
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        },
      });
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  private openUrgentDialog(rows: any[], urgent: boolean): void {
    this.unpaidPaymentsModal
      .openUrgentRequiredNote(urgent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.confirmed) return;

        this.submitting.set(true);
        this.api
          .updateUrgent(
            {
              filterId: this.filterId(),
              rows,
              note: result.note,
            },
            urgent
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.submitting.set(false);
              toast.success(urgent ? 'Marked as urgent.' : 'Urgent flag removed.');
              this.refreshData();
            },
            error: (error: any) => {
              this.submitting.set(false);
              toast.error(error.message || 'Unable to update urgent status.');
            },
          });
      });
  }

  private resolveRemoveContractId(rows: any[]): string {
    const codes = [
      ...new Set(
        rows
          .map((row) => this.getAccountCode(row))
          .filter((code) => code && code !== '-')
      ),
    ];
    return codes.length ? codes.join(', ') : '-';
  }

  private buildRemoveModalConfig(rows: any[]) {
    if (rows.length === 1) {
      return { contractId: this.resolveRemoveContractId(rows) };
    }
    return { paymentCount: rows.length };
  }

  private openRemoveDialog(rows: any[]): void {
    this.unpaidPaymentsModal
      .openRemoveUnpaid(this.buildRemoveModalConfig(rows))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.confirmed) return;

        this.submitting.set(true);
        this.api
          .removeFromUnpaid({
            filterId: this.filterId(),
            rows,
            note: result.note,
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.submitting.set(false);
              toast.success('Removed from unpaid.');
              this.refreshData();
            },
            error: (error: any) => {
              this.submitting.set(false);
              toast.error(error.message || 'Unable to remove rows from unpaid.');
            },
          });
      });
  }

  private buildExportRows(rows: any[]): Array<Record<string, string | number>> {
    if (this.view() === 'business') {
      return rows.map((row) => ({
        Name: this.getPrimaryName(row),
        Contact: this.getSecondaryName(row),
        Email: this.getEmail(row),
        Status: this.getStatusLabel(row),
        'Amount To Pay': this.getBusinessSummaryAmount(row),
        'Urgent Payments': this.getUrgentCount(row),
      }));
    }

    return rows.map((row) => ({
      ID: this.getAccountCode(row),
      'Business Name': this.getPrimaryName(row),
      'Owner / Contact': this.getSecondaryName(row),
      ...(this.isPartnerView() ? { 'Partner Email': this.getPartnerEmail(row) } : { Email: this.getEmail(row) }),
      Status: this.getStatusLabel(row),
      'Received Date': this.getReceivedDate(row),
      'Amount Deducted': this.getDeductedAmount(row),
      'Amount To Pay': this.getPayableAmount(row),
      Urgent: Number(row?.is_urgent) === 1 ? 'Yes' : 'No',
    }));
  }

  getLendeePayableAmount(row: any): number {
    return Number(row?.unpaid_details?.total_amount ?? row?.payout_amount ?? row?.pay_to_user ?? 0);
  }

  getLendeeShareType(row: any): string {
    const type = row?.unpaid_details?.share_type ?? row?.beneficiary_type ?? row?.share_type;
    if (type === 1 || type === 'business' || type === 'Business') return 'Business';
    if (type === 2 || type === 'supporter' || type === 'Supporter') return 'Supporter';
    if (type === 3 || type === 'unallocated') return 'Unallocated';
    return String(type || '-');
  }

  getLendeeStatus(row: any): string {
    const isPaid = row?.unpaid_details?.is_paid;
    if (isPaid === 1 || isPaid === true) return 'Paid';
    if (isPaid === 0 || isPaid === false) return 'Unpaid';
    const status = row?.unpaid_details?.status ?? row?.payout_status;
    if (status === 2 || String(status).toLowerCase() === 'paid') return 'Paid';
    if (status === 0 || status === 1 || String(status).toLowerCase() === 'unpaid') return 'Unpaid';
    return '-';
  }

  isLendeePaid(row: any): boolean {
    return this.getLendeeStatus(row) === 'Paid';
  }

  getLendeeContractCode(row: any): string {
    return row?.contract_details?.contract_code ?? row?.contract_code ?? '-';
  }

  getLendeeContractType(row: any): string {
    const type = row?.contract_details?.contract_type ?? row?.contract_type;
    if (type === 1) return '10% Platform Fee';
    if (type === 2) return 'No Fee Financing';
    if (type === 3) return 'In-House Financing';
    if (type === 4) return 'Zero Reserve Financing';
    if (type === 6) return 'EZ Payment Plan (With Deferred Interest)';
    return row?.contract_type_name ?? String(type || '-');
  }

  getLendeeEarlyPayoutAmount(row: any): number {
    return Number(row?.offer_details?.requested_amount ?? row?.doctor_details?.early_payout_amount ?? row?.requested_amount ?? 0);
  }

  getLendeeDuration(row: any): string {
    const months = row?.offer_details?.duration_months ?? row?.offer_details?.offer_duration_months ?? row?.contract_details?.term ?? row?.contract_details?.remaining_payments ?? row?.offer_duration_months ?? row?.duration;
    return months != null ? String(months) : '-';
  }

  getLendeePaymentReceived(row: any): number {
    return Number(
      row?.payment_details?.payment_received ??
        row?.payment_details?.paid_payments ??
        (row?.payment_details?.dopa_id ? 1 : row?.lup_payment_count ?? 0)
    );
  }

  getLendeePaidToSupporter(row: any): number {
    return Number(row?.unpaid_details?.is_paid ?? row?.paid_payments ?? 0);
  }

  getLendeeBankAdded(row: any): string {
    if (row?.stripe_account_id || row?.bank_added === 'Yes') return 'Yes';
    return '-';
  }

  readonly expandedLendeeRowId = signal<string | null>(null);

  toggleLendeeDetails(row: any): void {
    const id = this.getLendeeRowId(row);
    this.expandedLendeeRowId.set(this.expandedLendeeRowId() === id ? null : id);
  }

  isLendeeRowExpanded(row: any): boolean {
    return this.expandedLendeeRowId() === this.getLendeeRowId(row);
  }

  private getLendeeRowId(row: any): string {
    return String(row?.unpaid_details?.id ?? row?.offer_details?.id ?? row?.lup_id ?? '');
  }

  getLendeeContractState(row: any): string {
    const status = row?.contract_details?.pf_status ?? row?.pf_status;
    if (status === 1) return 'Initiated';
    if (status === 2) return 'Active';
    if (status === 3) return 'Completed';
    if (status === 4) return 'Cancelled';
    return '-';
  }

  getLendeeContractStateClass(row: any): string {
    const state = this.getLendeeContractState(row);
    if (state === 'Active') return 'text-[#15b268]';
    if (state === 'Completed') return 'text-[#0c66e8]';
    if (state === 'Cancelled') return 'text-[#f05050]';
    return 'text-[#7d7d93]';
  }

  getLendeeFinancedAmount(row: any): number {
    return Number(row?.contract_details?.financed_amount ?? row?.offer_details?.financed_amount ?? row?.financed_amount ?? 0);
  }

  getLendeeRemainingPayments(row: any): number {
    return Number(row?.contract_details?.remaining_payments ?? row?.remaining_payments ?? 0);
  }

  getLendeeBusinessOwnerName(row: any): string {
    return row?.doctor_details?.doctor_name ?? row?.doctor_name ?? '-';
  }

  getLendeePracticeName(row: any): string {
    return row?.doctor_details?.practice_name ?? row?.practice_name ?? '-';
  }

  getLendeeTransactionFee(row: any): number {
    return Number(row?.payment_details?.transaction_fee ?? row?.lendee_profit_amount ?? 0);
  }

  getLendeePaidToBusiness(row: any): string {
    const val = row?.payment_details?.pay_to_doctor ?? row?.pay_to_doctor;
    return val != null ? String(val) : 'N.A.';
  }

  getLendeePaymentDate(row: any): string {
    const date = row?.unpaid_details?.date_added ?? row?.payment_details?.date_added ?? row?.payment_details?.original_payment_date ?? row?.payout_date;
    if (!date) return '-';
    return this.calculationService.formatUnpaidTableDate(date);
  }

  getLendeePaymentTypeName(row: any): string {
    const type = row?.unpaid_details?.payment_type ?? row?.payment_type;
    if (type === 5) return 'Early Payout Amount';
    return row?.payment_details?.payment_type_name ?? 'Recurring Payment';
  }

  getLendeeAmountDeducted(row: any): number {
    return Number(row?.payment_details?.amount_deducted ?? row?.recurring_amount ?? 0);
  }

  getLendeeActivityLogs(row: any): string {
    return row?.unpaid_details?.remarks ?? row?.offer_details?.activity ?? '';
  }

  showLendeeViewLogs(row: any): void {
    const logs = this.getLendeeActivityLogs(row);
    if (!logs) {
      toast.info('No activity logs available for this record.');
      return;
    }
    this.unpaidPaymentsModal.openNotes({
      title: 'Activity Logs',
      entries: logs.split('\n').filter((l: string) => l.trim()).map((line: string) => ({
        userName: '',
        timestamp: '',
        action: '',
        note: line.trim(),
        sortKey: '',
      })),
      emptyMessage: 'No activity logs found.',
    });
  }

  onLendeeShareTypeChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.lendeeShareType.set(Number(raw));
    this.navigateWithState({ page: 1 });
  }

  onLendeePaymentStatusChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.lendeePaymentStatus.set(Number(raw));
    this.navigateWithState({ page: 1 });
  }

  private parseRouteState(params: ParamMap): RouteState {
    const versionType = this.parseInteger(params.get('version'), 0);
    const versionRange = getVersionDateRange(versionType);
    const filterId = this.parseInteger(params.get('unpaid_filter_id'), this.filterId());
    const normalizedFilterId = getDenefitsCategoryById(filterId)?.id ?? 1;
    const defaultCategory = getDenefitsCategoryKey(normalizedFilterId);
    const routeView = params.get('view') as DenefitsView | null;
    const view: DenefitsView =
      routeView === 'listing' || routeView === 'business' || routeView === 'business-details' || routeView === 'dashboard'
        ? routeView
        : 'dashboard';

    return {
      view,
      detailMode: params.get('detailMode') === 'contract' ? 'contract' : 'all',
      filterId: normalizedFilterId,
      category: params.get('category') || defaultCategory,
      entityId: params.get('id'),
      page: Math.max(1, this.parseInteger(params.get('page'), 1)),
      versionType,
      search: params.get('search') || '',
      statusFilter: params.get('status') || '0',
      vipOnly: params.get('vip') === '1',
      urgentOnly: params.get('urgent') === '1',
      orpFilter: this.parseInteger(params.get('orp'), -1),
      instantClaim: this.parseInteger(params.get('instant'), -1),
      paymentType: this.parseInteger(params.get('paymentType'), 0),
      challengeType: this.parseInteger(params.get('challenge'), 0),
      startDate: params.get('start') || versionRange.startDate,
      endDate: params.get('end') || versionRange.endDate,
      isDummy: parseQueryIsDummy(params.get('is_dummy'), getDefaultIsDummy()),
      lendeeShareType: this.parseInteger(params.get('lendee_share'), 0),
      lendeePaymentStatus: this.parseInteger(params.get('lendee_status'), 0),
    };
  }

  onIsDummyToggle(isDummy: boolean): void {
    this.navigateWithState({ isDummy });
  }

  private applyRouteState(state: RouteState): void {
    this.isDummyService.syncFromRoute(state.isDummy);
    this.view.set(state.view);
    this.detailMode.set(this.canCategoryUseContract(state.filterId) ? state.detailMode : 'all');
    this.filterId.set(state.filterId);
    this.category.set(state.category);
    this.entityId.set(state.entityId);
    this.page.set(state.page);
    this.versionType.set(state.versionType);
    this.search.set(state.search);
    this.statusFilter.set(state.statusFilter);
    this.vipOnly.set(state.vipOnly);
    this.urgentOnly.set(state.urgentOnly);
    this.orpFilter.set(state.orpFilter);
    this.instantClaim.set(state.instantClaim);
    this.paymentType.set(state.paymentType);
    this.challengeType.set(state.challengeType);
    const range = getVersionDateRange(state.versionType);
    const clamped = this.calculationService.clampUnpaidListDateRange(state.startDate, state.endDate, {
      min: range.startDate,
      max: range.endDate,
    });
    this.startDate.set(clamped.startDate);
    this.endDate.set(clamped.endDate);
    this.lendeeShareType.set(state.lendeeShareType);
    this.lendeePaymentStatus.set(state.lendeePaymentStatus);
    this.selectedIds.set([]);
  }

  private navigateWithState(patch: Partial<RouteState> & { view?: DenefitsView }): void {
    const next: RouteState = {
      view: patch.view ?? this.view(),
      detailMode: patch.detailMode ?? this.detailMode(),
      filterId: patch.filterId ?? this.filterId(),
      category: patch.category ?? this.category(),
      entityId: patch.entityId !== undefined ? patch.entityId : this.entityId(),
      page: patch.page ?? this.page(),
      versionType: patch.versionType ?? this.versionType(),
      search: patch.search ?? this.search(),
      statusFilter: patch.statusFilter ?? this.statusFilter(),
      vipOnly: patch.vipOnly ?? this.vipOnly(),
      urgentOnly: patch.urgentOnly ?? this.urgentOnly(),
      orpFilter: patch.orpFilter ?? this.orpFilter(),
      instantClaim: patch.instantClaim ?? this.instantClaim(),
      paymentType: patch.paymentType ?? this.paymentType(),
      challengeType: patch.challengeType ?? this.challengeType(),
      startDate: patch.startDate ?? this.startDate(),
      endDate: patch.endDate ?? this.endDate(),
      isDummy: patch.isDummy ?? this.isDummyService.isDummy(),
      lendeeShareType: patch.lendeeShareType ?? this.lendeeShareType(),
      lendeePaymentStatus: patch.lendeePaymentStatus ?? this.lendeePaymentStatus(),
    };

    const versionRange = getVersionDateRange(next.versionType);
    const queryParams: Record<string, string | number | null> = {
      view: next.view,
      unpaid_filter_id: next.view === 'dashboard' ? null : next.filterId,
      category: next.view === 'dashboard' ? null : next.category,
      id: next.view === 'business-details' ? next.entityId : null,
      detailMode: next.view === 'business-details' && this.canCategoryUseContract(next.filterId) ? next.detailMode : null,
      version: next.versionType || null,
      page: next.view === 'dashboard' ? null : next.page,
      search: next.search || null,
      status: next.statusFilter !== '0' ? next.statusFilter : null,
      vip: next.vipOnly ? 1 : null,
      urgent: next.urgentOnly ? 1 : null,
      orp: next.filterId === 2 && next.orpFilter !== -1 ? next.orpFilter : null,
      instant: next.filterId === 2 && next.instantClaim !== -1 ? next.instantClaim : null,
      paymentType: next.filterId === 3 && next.paymentType !== 0 ? next.paymentType : null,
      challenge: next.filterId === 4 && next.challengeType !== 0 ? next.challengeType : null,
      start: next.startDate !== versionRange.startDate ? next.startDate : null,
      end: next.endDate !== versionRange.endDate ? next.endDate : null,
      is_dummy: buildIsDummyQueryParam(next.isDummy, getDefaultIsDummy()),
      lendee_share: next.filterId === 12 && next.lendeeShareType !== 0 ? next.lendeeShareType : null,
      lendee_status: next.filterId === 12 && next.lendeePaymentStatus !== 0 ? next.lendeePaymentStatus : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  private canCategoryUseContract(filterId: number): boolean {
    return Boolean(getDenefitsCategoryById(filterId)?.supportsContractView);
  }

  private parseInteger(value: string | null, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

}
